// @flow

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import Layout from '../../layouts/default-logged-in';
import HomeNavigationTile from '../../home-navigation-tile';
import { PromptFooter } from '../../ai-prompt-management';
import * as ROUTES from '../../../constants/routes';
import { MAX_PROMPT_CHARS } from '../../ai-chat/chat/constants';

import type { t as ReportT } from '../../../models/report';

type Props = {
  history: any,
  myReports: ReportT[],
  orgReports: ReportT[],
};

const Page = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  width: 100%;
  background: var(--light-blue);
`;

const Center = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1.5rem 1rem;
  gap: 1rem;
`;

const TilesGrid = styled.div`
  --tile-size: min(clamp(225px, 27.5vw, 350px), clamp(140px, 28vh, 350px));
  --grid-gap: 16px;
  display: grid;
  grid-template-columns: repeat(2, var(--tile-size));
  gap: var(--grid-gap);
  justify-content: center;
  align-content: center;
  margin: 0 auto;

  @media (max-width: 480px) {
    grid-template-columns: var(--tile-size);
  }
`;

const AskLabel = styled.div`
  font-family: var(--fonts-default);
  color: var(--widget-heading-text);
  font-weight: 400;
  font-size: 1.5rem;
  margin-top: 1.2rem;
`;

const PromptWrap = styled.div`
  --tile-size: min(clamp(225px, 27.5vw, 350px), clamp(140px, 28vh, 350px));
  --grid-gap: 16px;
  width: 100%;
  max-width: calc((var(--tile-size) * 2) + var(--grid-gap));
  margin: 0 auto;
`;

const TILE_DEFAULT = {
  backgroundColorVar: '--white',
  textColorVar: '--widget-heading-text',
  iconColor: 'var(--primary)',
};

const TILE_HOVER = {
  backgroundColorVar: '--home-tile-hover',
  textColorVar: '--white',
  iconColor: 'var(--white)',
};

const isDraft = (r) => r && r.status === 'draft';

export default function HomePage({ history, myReports, orgReports }: Props) {
  const [prompt, setPrompt] = useState('');
  const inputRef = useRef(null);

  const promptTooLong = useMemo(
    () => String(prompt || '').length > MAX_PROMPT_CHARS,
    [prompt]
  );

  // Focus the prompt input on mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const resolveFirstMyReportRoute = useCallback(() => {
    const list = Array.isArray(myReports) ? myReports : [];
    const first = list.find((r) => r && r.id && !isDraft(r));
    // Navigate to the same route as the sidebar "My Reports" items
    return first && first.id ? `${ROUTES.MY_REPORTS}/${first.id}` : ROUTES.AI_REPORTS;
  }, [myReports]);

  const resolveFirstOrgReportRoute = useCallback(() => {
    const list = Array.isArray(orgReports) ? orgReports : [];
    const first = list.find((r) => r && r.id && !isDraft(r));
    // Navigate to the same route as the sidebar "Organisation Reports" items
    return first && first.id ? `${ROUTES.REPORTS}/${first.id}` : ROUTES.REPORTS;
  }, [orgReports]);

  const onSend = useCallback(() => {
    const trimmed = String(prompt || '').trim();
    if (!trimmed) return;
    if (promptTooLong) return;
    history.push(ROUTES.AI_CHAT, { initialPrompt: trimmed });
  }, [history, prompt, promptTooLong]);

  const onKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        onSend();
      }
    },
    [onSend]
  );

  return (
    <Layout>
      <Page>
        <Center>
          <TilesGrid>
            <HomeNavigationTile
              label={'Ask Aura'}
              tooltip={'Ask Aura - AI powered insights into your data'}
              to={ROUTES.AI_CHAT}
              defaultState={{
                ...TILE_DEFAULT,
                icon: 'chat',
                iconSize: 80,
              }}
              hoverState={{
                ...TILE_HOVER,
                icon: 'chat',
                iconSize: 85,
              }}
            />

            <HomeNavigationTile
              label={'Report\nDesigner'}
              tooltip={'Aura Visions AI powered report builder'}
              to={ROUTES.AI_REPORTS}
              defaultState={{
                ...TILE_DEFAULT,
                icon: 'draw',
                iconSize: 80,
              }}
              hoverState={{
                ...TILE_HOVER,
                icon: 'draw',
                iconSize: 85,
              }}
            />
          </TilesGrid>

          <AskLabel>Or just Ask Aura</AskLabel>

          <PromptWrap>
            <PromptFooter
              inputRef={inputRef}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={onKeyDown}
              onSend={onSend}
              placeholder={'Ask Aura anything...'}
              sendDisabled={promptTooLong}
              notice={
                promptTooLong
                  ? `Prompt is too long (${String(prompt || '').length}/${MAX_PROMPT_CHARS}). Please shorten your request.`
                  : undefined
              }
              helperText={''}
              attribution={''}
            />
          </PromptWrap>
        </Center>
      </Page>
    </Layout>
  );
}

